<template>
	<div
		class="page-blocks-list flex-fill"
		:class="{'page-blocks-list--dragging': sidebarDragUid}"
	>
		<div class="page-blocks-list-header">
			<button
				type="button"
				class="page-blocks-list-settings-btn"
				:title="_lang('Show in')"
				@click.stop="settingsOpen = !settingsOpen"
			>
				<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" /></svg>
			</button>
			<div
				v-if="settingsOpen"
				class="page-blocks-list-settings-menu"
				v-on-clickaway="closeSettings"
				@click.stop
			>
				<label>{{ _lang('Show in') }}</label>
				<select
					:value="blockListPosition"
					class="form-control form-control-sm"
					@change="$emit('update:blockListPosition', $event.target.value)"
				>
					<option value="left">{{ _lang('Left sidebar') }}</option>
					<option value="right">{{ _lang('Right sidebar') }}</option>
				</select>
			</div>
		</div>
		<p v-if="blockTree.length==0" class="nofound">{{ _lang('No blocks yet') }}</p>
		<div
			v-for="item in sidebarBlockTree"
			:key="treeKey(item)"
			class="page-block-row"
			:class="rowClass(item)"
			:data-block-uid="item.type === 'block' ? item.uid : null"
			:data-slot-parent="item.type === 'slot' && item.parentBlockUid ? item.parentBlockUid : null"
			:data-slot-index="item.type === 'slot' && item.slotIndex ? item.slotIndex : null"
			:data-root-end-slot="item.rootEnd ? '1' : null"
			:style="{ paddingLeft: (item.lvl * 12 + 10) + 'px' }"
		>
			<span v-if="item.type === 'slot'" class="page-block-slot-label">{{ slotLabel(item) }}</span>
			<a
				v-else
				class="page-block-item"
				href="#"
				@mouseenter="$emit('highlight', item.uid, 1)"
				@mouseleave="$emit('highlight', item.uid, 0)"
				@click.prevent="$emit('edit', item)"
			>
				<span
					v-if="!isMobile()"
					class="page-block-drag-handle"
					@mousedown.prevent.stop="$emit('drag-start', $event, item.uid)"
				>⠿</span>
				<span class="page-block-name">{{ item.name }}</span>
				<svg v-if="item.effect" viewBox="0 0 24 24"><path fill="currentColor" d="M15,2A7,7 0 0,1 22,9C22,11.71 20.46,14.05 18.22,15.22C17.55,16.5 16.5,17.55 15.22,18.22C14.05,20.46 11.71,22 9,22A7,7 0 0,1 2,15C2,12.29 3.54,9.95 5.78,8.78C6.45,7.5 7.5,6.45 8.78,5.78C9.95,3.54 12.29,2 15,2M12,19A7,7 0 0,1 5,12C4.37,12.84 4,13.87 4,15A5,5 0 0,0 9,20C10.13,20 11.16,19.63 12,19M15,16A7,7 0 0,1 8,9H8C7.37,9.84 7,10.87 7,12A5,5 0 0,0 12,17C13.13,17 14.16,16.63 15,16V16M15,4C13.87,4 12.84,4.37 12,5V5A7,7 0 0,1 19,12H19C19.63,11.16 20,10.13 20,9A5,5 0 0,0 15,4M10,9A5,5 0 0,0 15,14C15.6,14 16.17,13.9 16.7,13.7C16.9,13.17 17,12.6 17,12A5,5 0 0,0 12,7C11.4,7 10.83,7.1 10.3,7.3C10.1,7.83 10,8.4 10,9Z" /></svg>
			</a>
		</div>
	</div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

export default {
	mixins:[clickaway],
	props:{
		sidebarDragUid:{ type:String, default:'' },
		blockTree:{ type:Array, default:()=>[] },
		sidebarBlockTree:{ type:Array, default:()=>[] },
		blockListPosition:{ type:String, default:'left' },
		rowClass:{ type:Function, required:true },
		treeKey:{ type:Function, required:true },
		slotLabel:{ type:Function, required:true },
	},
	data(){
		return {
			settingsOpen:false,
		};
	},
	methods:{
		isMobile(){
			return window.isMobile();
		},
		closeSettings(){
			this.settingsOpen = false;
		},
	},
}
</script>
